import React, { useState } from 'react';
import { Form, Button, Table, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faComments,
  faFileAlt, faChevronDown, faChevronUp,
  faFilter, faFileExcel, faInfoCircle, faCircle,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import axios from 'axios';
import moment from 'moment';
import XLSX from 'xlsx-js-style';
import stateDistrictTalukaData from '../../assets/static/IndiaStateDistrictTaluka';

// ─── Report tree definition ───────────────────────────────────────────────────

const REPORT_TREE = [
  {
    id: 'farmer_profile',
    label: 'Farmer & Field Reports',
    icon: faUsers,
    items: [
      { id: 'village_farmer',   label: 'Village-wise Farmer Report' },
      { id: 'taluka_farmer',    label: 'Taluka-wise Farmer Report' },
      { id: 'major_crop',       label: 'Major Crop Report (State / District / Taluka)' },
      {
        id: 'land_size', label: 'Land Size Category Report', expandable: true,
        subItems: [
          { id: 'land_marginal', label: 'Marginal Farmers (< 1 ha)' },
          { id: 'land_small',    label: 'Small Farmers (1–2 ha)' },
          { id: 'land_medium',   label: 'Medium Farmers (2–5 ha)' },
          { id: 'land_large',    label: 'Large Farmers (> 5 ha)' },
        ],
      },
      { id: 'crop_farmer',      label: 'Crop-wise Farmer Report' },
      { id: 'farmer_category',  label: 'Farmer Category Report' },
      { id: 'village_staff',    label: 'Village-wise Staff Report' },
      { id: 'taluka_staff',     label: 'Taluka-wise Staff Report' },
      { id: 'district_staff',   label: 'District-wise Staff Report' },
      { id: 'village_dealer',   label: 'Village-wise Shops / Dealers Report' },
      { id: 'taluka_dealer',    label: 'Taluka-wise Shops / Dealers Report' },
      { id: 'district_dealer',  label: 'District-wise Shops / Dealers Report' },
    ],
  },
  {
    id: 'query_advisory',
    label: 'Query / Advisory Reports',
    icon: faComments,
    items: [
      { id: 'open_queries',           label: 'Open Queries Report' },
      { id: 'closed_queries',         label: 'Closed Queries Report' },
      { id: 'pending_queries',        label: 'Pending Queries Report' },
      { id: 'query_category',         label: 'Query Category Report' },
      { id: 'crop_query',             label: 'Crop-wise Query Report' },
      { id: 'village_query',          label: 'Village-wise Query Report' },
      { id: 'taluka_query',           label: 'Taluka-wise Query Report' },
      { id: 'district_query',         label: 'District-wise Query Report' },
      { id: 'query_matching',         label: 'Query Matching Report (Matched / Not Matched)' },
      { id: 'query_source',           label: 'Query Source Report (Online / On-field)' },
      { id: 'customer_satisfaction',  label: 'Customer Satisfaction Report' },
    ],
  },
];

// ─── Filter config per report type ───────────────────────────────────────────

const FILTER_FIELDS = {
  village_farmer:        ['state','district','taluka','village','crop','category','dateFrom','dateTo'],
  taluka_farmer:         ['state','district','taluka','crop','category','dateFrom','dateTo'],
  major_crop:            ['state','district','taluka','crop','category','dateFrom','dateTo'],
  land_marginal:         ['state','district','taluka','village','category','dateFrom','dateTo'],
  land_small:            ['state','district','taluka','village','category','dateFrom','dateTo'],
  land_medium:           ['state','district','taluka','village','category','dateFrom','dateTo'],
  land_large:            ['state','district','taluka','village','category','dateFrom','dateTo'],
  crop_farmer:           ['state','district','taluka','crop','category','dateFrom','dateTo'],
  farmer_category:       ['state','district','taluka','village','category','dateFrom','dateTo'],
  village_staff:         ['state','district','taluka','village'],
  taluka_staff:          ['state','district','taluka'],
  district_staff:        ['state','district'],
  village_dealer:        ['state','district','taluka','village'],
  taluka_dealer:         ['state','district','taluka'],
  district_dealer:       ['state','district'],
  open_queries:          ['state','district','taluka','village','crop','dateFrom','dateTo'],
  closed_queries:        ['state','district','taluka','village','crop','dateFrom','dateTo'],
  pending_queries:       ['state','district','taluka','village','dateFrom','dateTo'],
  query_category:        ['state','district','taluka','crop','dateFrom','dateTo'],
  crop_query:            ['state','district','taluka','crop','dateFrom','dateTo'],
  village_query:         ['state','district','taluka','village','dateFrom','dateTo'],
  taluka_query:          ['state','district','taluka','dateFrom','dateTo'],
  district_query:        ['state','district','dateFrom','dateTo'],
  query_matching:        ['state','district','taluka','village','verification','dateFrom','dateTo'],
  query_source:          ['state','district','taluka','source','dateFrom','dateTo'],
  customer_satisfaction: ['state','district','taluka','village','dateFrom','dateTo'],
};

// ─── Column config per report type ───────────────────────────────────────────

const FARMER_COLS = [
  { key: 'regNo',                      label: 'Reg No' },
  { key: 'fullName',                   label: 'Farmer Name' },
  { key: 'mobileNumber',               label: 'Mobile' },
  { key: 'townVillage',                label: 'Village' },
  { key: 'taluka',                     label: 'Taluka' },
  { key: 'district',                   label: 'District' },
  { key: 'state',                      label: 'State' },
  { key: 'currentCropsCultivated',     label: 'Current Crops' },
  { key: 'landAcreage',                label: 'Land (Ha)' },
  { key: 'paid',                       label: 'Paid' },
  { key: 'existingCustomer',           label: 'Existing' },
  { key: 'soilTestingStatus',          label: 'Soil Test' },
  { key: 'createdAt',                  label: 'Registered On', isDate: true },
];

const STAFF_COLS = [
  { key: 'fullName',      label: 'Staff Name' },
  { key: 'mobileNumber',  label: 'Mobile' },
  { key: 'designation',   label: 'Designation' },
  { key: 'village',       label: 'Village' },
  { key: 'taluka',        label: 'Taluka' },
  { key: 'district',      label: 'District' },
  { key: 'state',         label: 'State' },
  { key: 'headquarter',   label: 'Headquarter' },
  { key: 'dateOfJoining', label: 'Joined', isDate: true },
  { key: 'status',        label: 'Status' },
];

const DEALER_COLS = [
  { key: 'shopName',              label: 'Shop Name' },
  { key: 'ownerName',             label: 'Owner' },
  { key: 'mobileNumber',          label: 'Mobile' },
  { key: 'village',               label: 'Village' },
  { key: 'tehsil',                label: 'Taluka' },
  { key: 'district',              label: 'District' },
  { key: 'state',                 label: 'State' },
  { key: 'dealerType',            label: 'Type' },
  { key: 'assignedSalesOfficer',  label: 'Sales Officer' },
  { key: 'status',                label: 'Status' },
];

const ISSUE_COLS = [
  { key: 'issueCode',     label: 'Query Code' },
  { key: 'farmerName',    label: 'Farmer Name' },
  { key: 'farmerMobile',  label: 'Mobile' },
  { key: 'townVillage',   label: 'Village' },
  { key: 'taluka',        label: 'Taluka' },
  { key: 'district',      label: 'District' },
  { key: 'issueStatus',   label: 'Status' },
  { key: 'issueType',     label: 'Type' },
  { key: 'issueCrop',     label: 'Crop' },
  { key: 'issueCategory', label: 'Category' },
  { key: 'issueAssignee', label: 'Assigned To' },
  { key: 'issueCreatedAt',label: 'Date', isDate: true },
];

const REPORT_COLUMNS = {
  village_farmer: FARMER_COLS, taluka_farmer: FARMER_COLS, major_crop: FARMER_COLS,
  land_marginal: FARMER_COLS, land_small: FARMER_COLS, land_medium: FARMER_COLS, land_large: FARMER_COLS,
  crop_farmer: FARMER_COLS, farmer_category: FARMER_COLS,
  village_staff: STAFF_COLS, taluka_staff: STAFF_COLS, district_staff: STAFF_COLS,
  village_dealer: DEALER_COLS, taluka_dealer: DEALER_COLS, district_dealer: DEALER_COLS,
  open_queries: ISSUE_COLS, closed_queries: ISSUE_COLS, pending_queries: ISSUE_COLS,
  query_category: ISSUE_COLS, crop_query: ISSUE_COLS, village_query: ISSUE_COLS,
  taluka_query: ISSUE_COLS, district_query: ISSUE_COLS,
  query_matching:        [...ISSUE_COLS, { key: 'issueVerification', label: 'Match Result' }],
  query_source:          [...ISSUE_COLS, { key: 'phonecallio',       label: 'Source' }],
  customer_satisfaction: [...ISSUE_COLS, { key: 'manshyaFeedback',   label: 'Feedback' }],
};

// ─── Category label lookup ────────────────────────────────────────────────────

function findReportMeta(reportTypeId) {
  for (const cat of REPORT_TREE) {
    for (const item of cat.items) {
      if (item.id === reportTypeId) return { categoryId: cat.id, categoryLabel: cat.label, reportLabel: item.label };
      if (item.subItems) {
        const sub = item.subItems.find(s => s.id === reportTypeId);
        if (sub) return { categoryId: cat.id, categoryLabel: cat.label, reportLabel: sub.label };
      }
    }
  }
  return null;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const C = {
  dark: '#1a3328', mid: '#2d5a3d', bright: '#3d8c5e',
  light: '#e8f5ee', page: '#f4f6f5', border: '#d8e8dc',
  white: '#ffffff', textDk: '#1a2e22', textGr: '#6c757d',
  active: '#e8f5ee', activeBorder: '#2d5a3d',
};

const PAGE_SIZE = 20;

// ─── Component ────────────────────────────────────────────────────────────────

function ReportCenter() {
  const [expandedCats, setExpandedCats] = useState({ farmer_profile: true });
  const [expandedSubItems, setExpandedSubItems] = useState({});
  const [selectedType, setSelectedType] = useState(null);  // reportType id string
  const [filters, setFilters] = useState({});
  const [districtOpts, setDistrictOpts] = useState([]);
  const [talukaOpts, setTalukaOpts] = useState([]);
  const [results, setResults] = useState(null);   // null = not yet run
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const meta = selectedType ? findReportMeta(selectedType) : null;
  const activeFields = selectedType ? (FILTER_FIELDS[selectedType] || []) : [];
  const columns = selectedType ? (REPORT_COLUMNS[selectedType] || []) : [];

  // ── Sidebar helpers ─────────────────────────────────────────────────────────

  const toggleCat = (id) => setExpandedCats(p => ({ ...p, [id]: !p[id] }));
  const toggleSub = (id) => setExpandedSubItems(p => ({ ...p, [id]: !p[id] }));

  const selectReport = (typeId) => {
    setSelectedType(typeId);
    setFilters({});
    setDistrictOpts([]);
    setTalukaOpts([]);
    setResults(null);
    setError(null);
    setCurrentPage(1);
  };

  // ── Filter handlers ─────────────────────────────────────────────────────────

  const handleStateChange = (opt) => {
    setFilters(p => ({ ...p, state: opt ? opt.value : '', district: '', taluka: '' }));
    setDistrictOpts(opt ? Object.keys(stateDistrictTalukaData[opt.value] || {}).map(d => ({ value: d, label: d })) : []);
    setTalukaOpts([]);
  };

  const handleDistrictChange = (opt) => {
    setFilters(p => ({ ...p, district: opt ? opt.value : '', taluka: '' }));
    if (opt && filters.state) {
      const ts = stateDistrictTalukaData[filters.state]?.[opt.value] || [];
      setTalukaOpts(ts.map(t => ({ value: t, label: t })));
    } else {
      setTalukaOpts([]);
    }
  };

  const setFilter = (key, value) => setFilters(p => ({ ...p, [key]: value }));

  // ── Generate report ─────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!meta) return;
    setLoading(true);
    setError(null);
    setResults(null);
    setCurrentPage(1);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/reports/generate`, {
        reportCategory: meta.categoryId,
        reportType: selectedType,
        filters,
      });

      if (res.data.success) {
        setResults(res.data.data);
      } else {
        setError(res.data.message || 'Failed to generate report.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Excel export ────────────────────────────────────────────────────────────

  const getBorder = () => ({
    top: { style: 'thin', color: { rgb: '000000' } },
    bottom: { style: 'thin', color: { rgb: '000000' } },
    left: { style: 'thin', color: { rgb: '000000' } },
    right: { style: 'thin', color: { rgb: '000000' } },
  });

  const handleExport = () => {
    if (!results || results.length === 0) { alert('No data to export.'); return; }
    const wb = XLSX.utils.book_new();
    const wsData = [
      columns.map(c => ({ v: c.label, s: { font: { bold: true }, border: getBorder() } })),
      ...results.map(row =>
        columns.map(c => {
          const val = c.isDate ? (row[c.key] ? moment(row[c.key]).format('DD-MMM-YYYY') : '') : (row[c.key] ?? '');
          return { v: String(val), s: { border: getBorder() } };
        })
      ),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = wsData[0].map((_, i) => ({
      wch: Math.max(...wsData.map(r => (r[i] && r[i].v ? String(r[i].v).length : 0))) + 2,
    }));
    ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2' };
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${meta?.reportLabel || 'report'}_${moment().format('DDMMMYYYY')}.xlsx`);
  };

  // ── Pagination ──────────────────────────────────────────────────────────────

  const totalPages = results ? Math.ceil(results.length / PAGE_SIZE) : 0;
  const pageRows = results ? results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE) : [];

  const cellVal = (row, col) => {
    const v = row[col.key];
    if (v == null || v === '') return '—';
    if (col.isDate) return moment(v).isValid() ? moment(v).format('DD-MMM-YYYY') : v;
    return String(v);
  };

  const stateOpts = Object.keys(stateDistrictTalukaData).map(s => ({ value: s, label: s }));

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderFilterPanel = () => {
    if (!selectedType) return null;

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px' }}>
        {activeFields.includes('state') && (
          <div style={{ minWidth: 160, flex: '1 1 160px' }}>
            <Form.Label style={labelStyle}>State</Form.Label>
            <Select
              placeholder="Select State"
              options={stateOpts}
              value={filters.state ? { value: filters.state, label: filters.state } : null}
              onChange={handleStateChange}
              isClearable
              styles={selectStyles}
            />
          </div>
        )}
        {activeFields.includes('district') && (
          <div style={{ minWidth: 160, flex: '1 1 160px' }}>
            <Form.Label style={labelStyle}>District</Form.Label>
            <Select
              placeholder="Select District"
              options={districtOpts}
              value={filters.district ? { value: filters.district, label: filters.district } : null}
              onChange={(opt) => handleDistrictChange(opt)}
              isDisabled={!filters.state}
              isClearable
              styles={selectStyles}
            />
          </div>
        )}
        {activeFields.includes('taluka') && (
          <div style={{ minWidth: 160, flex: '1 1 160px' }}>
            <Form.Label style={labelStyle}>Taluka</Form.Label>
            <Select
              placeholder="Select Taluka"
              options={talukaOpts}
              value={filters.taluka ? { value: filters.taluka, label: filters.taluka } : null}
              onChange={(opt) => setFilter('taluka', opt ? opt.value : '')}
              isDisabled={!filters.district}
              isClearable
              styles={selectStyles}
            />
          </div>
        )}
        {activeFields.includes('village') && (
          <div style={{ minWidth: 150, flex: '1 1 150px' }}>
            <Form.Label style={labelStyle}>Village</Form.Label>
            <Form.Control size="sm" placeholder="Enter village" value={filters.village || ''} onChange={e => setFilter('village', e.target.value)} />
          </div>
        )}
        {activeFields.includes('crop') && (
          <div style={{ minWidth: 150, flex: '1 1 150px' }}>
            <Form.Label style={labelStyle}>Crop</Form.Label>
            <Form.Control size="sm" placeholder="Enter crop name" value={filters.crop || ''} onChange={e => setFilter('crop', e.target.value)} />
          </div>
        )}
        {activeFields.includes('verification') && (
          <div style={{ minWidth: 150, flex: '1 1 150px' }}>
            <Form.Label style={labelStyle}>Match Status</Form.Label>
            <Select
              placeholder="All"
              options={[{ value: 'matched', label: 'Matched' }, { value: 'unmatched', label: 'Not Matched' }]}
              value={filters.verification ? { value: filters.verification, label: filters.verification === 'matched' ? 'Matched' : 'Not Matched' } : null}
              onChange={opt => setFilter('verification', opt ? opt.value : '')}
              isClearable
              styles={selectStyles}
            />
          </div>
        )}
        {activeFields.includes('source') && (
          <div style={{ minWidth: 150, flex: '1 1 150px' }}>
            <Form.Label style={labelStyle}>Source</Form.Label>
            <Select
              placeholder="All"
              options={[{ value: 'Incoming', label: 'Online (Incoming)' }, { value: 'Outgoing', label: 'On-field (Outgoing)' }]}
              value={filters.source ? { value: filters.source, label: filters.source === 'Incoming' ? 'Online (Incoming)' : 'On-field (Outgoing)' } : null}
              onChange={opt => setFilter('source', opt ? opt.value : '')}
              isClearable
              styles={selectStyles}
            />
          </div>
        )}
        {activeFields.includes('category') && (
          <div style={{ minWidth: 160, flex: '1 1 160px' }}>
            <Form.Label style={labelStyle}>Farmer Category</Form.Label>
            <Select
              placeholder="All Categories"
              options={[
                { value: 'gold',   label: '🥇 Gold Farmer'   },
                { value: 'silver', label: '🥈 Silver Farmer' },
                { value: 'raw',    label: '🌱 Raw Farmer'    },
              ]}
              value={filters.category ? { value: filters.category, label: filters.category === 'gold' ? '🥇 Gold Farmer' : filters.category === 'silver' ? '🥈 Silver Farmer' : '🌱 Raw Farmer' } : null}
              onChange={opt => setFilter('category', opt ? opt.value : '')}
              isClearable
              styles={selectStyles}
            />
          </div>
        )}
        {activeFields.includes('dateFrom') && (
          <div style={{ minWidth: 140, flex: '1 1 140px' }}>
            <Form.Label style={labelStyle}>Date From</Form.Label>
            <Form.Control size="sm" type="date" value={filters.dateFrom || ''} onChange={e => setFilter('dateFrom', e.target.value)} />
          </div>
        )}
        {activeFields.includes('dateTo') && (
          <div style={{ minWidth: 140, flex: '1 1 140px' }}>
            <Form.Label style={labelStyle}>Date To</Form.Label>
            <Form.Control size="sm" type="date" value={filters.dateTo || ''} onChange={e => setFilter('dateTo', e.target.value)} />
          </div>
        )}
      </div>
    );
  };

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: C.page }}>

      {/* ── Page Header ── */}
      <div style={{
        backgroundColor: C.white, padding: '0.85rem 1.5rem',
        display: 'flex', alignItems: 'center',
        borderBottom: `1px solid ${C.border}`,
        boxShadow: '0 1px 4px rgba(20,50,30,0.06)', flexShrink: 0,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9, backgroundColor: C.light,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12,
        }}>
          <FontAwesomeIcon icon={faFileAlt} style={{ color: C.mid, fontSize: '0.9rem' }} />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: C.textDk }}>Reports</div>
          <div style={{ fontSize: '0.72rem', color: C.textGr }}>Generate and export reports</div>
        </div>
      </div>

      {/* ── Body: Sidebar + Right Panel ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* ── Left Sidebar ── */}
      <div style={{
        width: 360, flexShrink: 0, height: '100%', overflowY: 'auto',
        backgroundColor: C.white, borderRight: `1px solid ${C.border}`,
      }}>
        {REPORT_TREE.map(cat => (
          <div key={cat.id}>
            {/* Category header */}
            <div
              onClick={() => toggleCat(cat.id)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', cursor: 'pointer',
                borderBottom: `1px solid ${C.border}`,
                backgroundColor: expandedCats[cat.id] ? '#f7fbf8' : C.white,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 7, backgroundColor: C.light,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FontAwesomeIcon icon={cat.icon} style={{ color: C.mid, fontSize: '0.75rem' }} />
                </div>
                <span style={{ fontWeight: 700, fontSize: '0.82rem', color: C.textDk }}>{cat.label}</span>
              </div>
              <FontAwesomeIcon
                icon={expandedCats[cat.id] ? faChevronUp : faChevronDown}
                style={{ color: C.textGr, fontSize: '0.7rem' }}
              />
            </div>

            {/* Items */}
            {expandedCats[cat.id] && (
              <div style={{ borderBottom: `1px solid ${C.border}` }}>
                {cat.items.map(item => (
                  <div key={item.id}>
                    {item.subItems ? (
                      <>
                        {/* Expandable parent item */}
                        <div
                          onClick={() => toggleSub(item.id)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '8px 16px 8px 20px', cursor: 'pointer',
                            backgroundColor: C.white,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FontAwesomeIcon icon={faFileAlt} style={{ color: C.textGr, fontSize: '0.7rem' }} />
                            <span style={{ fontSize: '0.8rem', color: C.textDk }}>{item.label}</span>
                          </div>
                          <FontAwesomeIcon
                            icon={expandedSubItems[item.id] ? faChevronUp : faChevronDown}
                            style={{ color: C.textGr, fontSize: '0.65rem' }}
                          />
                        </div>
                        {expandedSubItems[item.id] && item.subItems.map(sub => (
                          <div
                            key={sub.id}
                            onClick={() => { selectReport(sub.id); if (!expandedCats[cat.id]) toggleCat(cat.id); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '7px 16px 7px 36px', cursor: 'pointer',
                              backgroundColor: selectedType === sub.id ? C.active : C.white,
                              borderLeft: selectedType === sub.id ? `3px solid ${C.activeBorder}` : '3px solid transparent',
                            }}
                          >
                            <FontAwesomeIcon icon={faCircle} style={{ color: C.mid, fontSize: '0.35rem' }} />
                            <span style={{ fontSize: '0.78rem', color: selectedType === sub.id ? C.mid : C.textDk, fontWeight: selectedType === sub.id ? 600 : 400 }}>
                              {sub.label}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div
                        onClick={() => selectReport(item.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 16px 8px 20px', cursor: 'pointer',
                          backgroundColor: selectedType === item.id ? C.active : C.white,
                          borderLeft: selectedType === item.id ? `3px solid ${C.activeBorder}` : '3px solid transparent',
                        }}
                      >
                        <FontAwesomeIcon icon={faFileAlt} style={{ color: selectedType === item.id ? C.mid : C.textGr, fontSize: '0.7rem' }} />
                        <span style={{ fontSize: '0.8rem', color: selectedType === item.id ? C.mid : C.textDk, fontWeight: selectedType === item.id ? 600 : 400 }}>
                          {item.label}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Right Panel ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>

        {/* Report Options card */}
        <div style={{
          backgroundColor: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
          padding: '1.25rem', marginBottom: '1.25rem',
          boxShadow: '0 1px 4px rgba(20,50,30,0.06)',
        }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: C.textDk, marginBottom: '1rem' }}>
            Report Options
          </div>

          {/* Info bar */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9',
            borderRadius: 6, padding: '8px 12px', marginBottom: '1rem', fontSize: '0.78rem', color: '#2e7d32',
          }}>
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              {selectedType
                ? `Showing filters for: ${meta?.categoryLabel} → ${meta?.reportLabel}`
                : 'Select a report from the left panel to view applicable filters.'}
            </span>
          </div>

          {/* Divider */}
          <div style={{ borderTop: `1px dashed ${C.border}`, margin: '0.75rem 0' }} />

          {/* Filters section */}
          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: C.textDk, marginBottom: '0.75rem' }}>
            Filters
            <span style={{ fontWeight: 400, color: C.textGr, marginLeft: 6, fontSize: '0.75rem' }}>
              (Applicable to Selected Report)
            </span>
          </div>

          {!selectedType ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: '2.5rem 1rem', color: C.textGr,
            }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%', backgroundColor: C.light,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem',
              }}>
                <FontAwesomeIcon icon={faFilter} style={{ fontSize: '1.4rem', color: '#9dbfa9' }} />
              </div>
              <div style={{ textAlign: 'center', fontSize: '0.82rem' }}>
                Select a report from the left panel<br />to view applicable filters.
              </div>
            </div>
          ) : (
            renderFilterPanel()
          )}
        </div>

        {/* Generate + Download */}
        <div style={{
          backgroundColor: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
          padding: '1rem 1.25rem', marginBottom: '1.25rem',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10,
          boxShadow: '0 1px 4px rgba(20,50,30,0.06)',
        }}>
          {results && results.length > 0 && (
            <Button
              onClick={handleExport}
              style={{
                backgroundColor: '#1e8449', border: 'none',
                padding: '8px 20px', fontWeight: 600, fontSize: '0.88rem',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <FontAwesomeIcon icon={faFileExcel} /> Download as Excel
            </Button>
          )}
          <Button
            onClick={handleGenerate}
            disabled={!selectedType || loading}
            style={{
              backgroundColor: loading ? '#6c757d' : C.mid, border: 'none',
              padding: '8px 24px', fontWeight: 600, fontSize: '0.88rem',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <FontAwesomeIcon icon={faFileAlt} />
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: '10px 14px', backgroundColor: '#fdecea', border: '1px solid #ef9a9a', borderRadius: 6, color: '#c62828', fontSize: '0.82rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Results */}
        {results !== null && !loading && (
          <div style={{
            backgroundColor: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
            boxShadow: '0 1px 4px rgba(20,50,30,0.06)', overflow: 'hidden',
          }}>
            {/* Results header */}
            <div style={{
              padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              backgroundColor: '#f7fbf8',
            }}>
              <span style={{ fontWeight: 600, fontSize: '0.85rem', color: C.textDk }}>
                {meta?.reportLabel} — <span style={{ color: C.mid }}>{results.length} record{results.length !== 1 ? 's' : ''}</span>
              </span>
            </div>

            {results.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: C.textGr, fontSize: '0.85rem' }}>
                No records found for the selected filters.
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <Table striped bordered hover size="sm" style={{ margin: 0, fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f7fbf8' }}>
                        <th style={{ whiteSpace: 'nowrap', padding: '8px 10px' }}>#</th>
                        {columns.map(c => (
                          <th key={c.key} style={{ whiteSpace: 'nowrap', padding: '8px 10px', color: C.textDk }}>
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((row, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                            {(currentPage - 1) * PAGE_SIZE + idx + 1}
                          </td>
                          {columns.map(c => (
                            <td key={c.key} style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                              {cellVal(row, c)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0', borderTop: `1px solid ${C.border}` }}>
                    <Pagination size="sm">
                      <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                      <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                      {Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
                        const n = Math.max(1, currentPage - 4) + i;
                        return n <= totalPages ? (
                          <Pagination.Item key={n} active={n === currentPage} onClick={() => setCurrentPage(n)}>{n}</Pagination.Item>
                        ) : null;
                      })}
                      <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                      <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ─── Small style constants ────────────────────────────────────────────────────

const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#1a2e22', marginBottom: 4, display: 'block' };

const formatBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600,
  border: '1.5px solid #ddd', backgroundColor: '#fff', cursor: 'pointer',
};

const selectStyles = {
  control: (b) => ({ ...b, minHeight: 32, fontSize: '0.82rem' }),
  menu: (b) => ({ ...b, fontSize: '0.82rem', zIndex: 9999 }),
};

export default ReportCenter;
